import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Box,
  Checkbox,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import './ManageCodingForms.scss';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import {
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import {
  codingFormsDetailsMoreMenu,
  manageCodingFormsColumns,
} from '../codingFormsUtils';
import {ReactComponent as NoDataIcon} from '../../../assets/images/no-data-found.svg';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../common/arr-menu/ArrMenu';
import {ReactComponent as CheckedIcon} from '../../../../src/assets/images/icon-checked.svg';
import {ReactComponent as MoreIcon} from '../../../assets/images/icon-more.svg';
import {ReactComponent as UnCheckedIcon} from '../../../../src/assets/images/icon-unchecked.svg';
import {
  ICodingFormDetailsForEditResponse,
  ICodingListGridData,
} from '../../../api/types/document.type';
import ArrPagination from '../../common/arr-pagination/ArrPagination';
import ArrButton from '../../common/arr-button/ArrButton';
import {t} from 'i18next';
import {
  useCanDeleteCodingForm,
  useCodingFormGridList,
  useCodingFormDetailsforEdit,
} from '../../../hooks/document/useDocument';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../shared/constants';
import CopyCodingFormModal from './CopyCodingFormModal';
import DeleteCodingFormModal from './DeleteCodingFormModal';
import {SecurityGroupCodingForm} from '../security-group-coding-form/SecurityGroupCodingForm';
import ArrIconButton from '../../common/arr-icon-button/ArrIconButton';

const NoRowsOverlay = () => (
  <Box className="no-project-data-found">
    <NoDataIcon />
    <Typography
      className="typo-text headline3_size no-project-text"
      data-testid={`manage-coding-forms-no-rows`}>
      {t('codingForms.grid.noRows')}
    </Typography>
  </Box>
);

const ColumnMenuItem: React.FC<CustomMenuItem> = React.memo(
  ({id, label, checked, disabled, onToggle}) => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled) return;
      onToggle?.();
    };

    return (
      <MenuItem
        dense
        disableRipple
        disabled={disabled}
        onClick={handleClick}
        role="menuitemcheckbox"
        aria-checked={checked}
        aria-disabled={disabled}
        data-testid={`${id}-manage-coding-forms-menu-item`}
      >
        <ListItemIcon>
          <Checkbox
            data-testid={`manage-coding-forms-column-menu-checkbox-${id}`}
            edge="start"
            checked={checked}
            tabIndex={-1}
            disableRipple
            onClick={(e) => {
              e.stopPropagation();
              onToggle?.();
            }}
            size="small"
            icon={<UnCheckedIcon />}
            checkedIcon={<CheckedIcon />}
            disabled={disabled}
          />
        </ListItemIcon>
        <ListItemText primary={label} />
      </MenuItem>
    );
  }
);

type ManageCodingFormsProps = {
  searchText: string;
  onEditForm?: (formData: ICodingFormDetailsForEditResponse['data']) => void;
};

const ROW_HEIGHT = 36;

const ManageCodingForms = ({
  searchText,
  onEditForm,
}: ManageCodingFormsProps) => {
  const codingFormDetailsMutation = useCodingFormDetailsforEdit(
    '',
    undefined,
    undefined
  );

  const [pageNumber, setPageNumber] = useState(1);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToastNotification, setShowToastNotification] =
    useState<boolean>(false);
  const [toastNotificationMessage, setToastNotificationMessage] =
    useState<string>('');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openCopyModal, setOpenCopyModal] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [rows, setRows] = useState<ICodingListGridData[]>([]);
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const [selectedForm, setSelectedForm] = useState<ICodingListGridData | null>(
    null
  );
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'codingFormName',
      sort: 'asc',
    },
  ]);

  const [isSecurityGroupForm, setIsSecurityGroupForm] =
    useState<boolean>(false);

  type ColumnDef = ReturnType<typeof manageCodingFormsColumns>[number];
  type FieldKey = ColumnDef['field'];

  const baseColumns = useMemo<ColumnDef[]>(
    () => manageCodingFormsColumns(),
    []
  );

  const [visibility, setVisibility] = useState<GridColumnVisibilityModel>(() =>
    baseColumns.reduce((acc, c) => ({...acc, [c.field]: true}), {})
  );

  const queryKeyParams = useMemo(
    () =>
      `?CodingFormName=${searchText}&Pagesize=${itemsPerPage}&PageNumber=${pageNumber}&SortingOrder=${sortModel[0]?.sort || 'asc'}&SortingColumn=${sortModel[0]?.field || 'codingFormName'}&Echo=1`,
    [itemsPerPage, pageNumber, sortModel, searchText]
  );

  const {
    data: codingFormList,
    isLoading: isCodingFormListLoading,
    refetch: refetchCodingFormList,
  } = useCodingFormGridList(queryKeyParams);

  useEffect(() => {
    setRows(codingFormList?.data ?? []);
  }, [codingFormList]);

  const handleOnSuccess = (successMsg: string) => {
    setToastNotificationMessage(successMsg);
    setShowToastNotification(true);
  };

  const {mutate: canDeleteCodingForm} = useCanDeleteCodingForm();

  const visibleCount = useMemo(
    () => Object.values(visibility).filter(Boolean).length,
    [visibility]
  );

  const handleDeleteClick = (codingFormId: string) => {
    canDeleteCodingForm(codingFormId, {
      onSuccess: (data) => {
        if (data.success) {
          setOpenDeleteModal(true);
        } else {
          triggerToast(data.message, [ToastType.Error]);
        }
      },
      onError: (data, error) => {
        triggerToast(data.message || error, [ToastType.Error]);
      },
    });
  };
  const toggle = useCallback(
    (field: FieldKey) => {
      setVisibility((prev) => {
        const currentlyVisible = Boolean(prev[field]);
        const currentlyVisibleCount =
          Object.values(prev).filter(Boolean).length;

        if (currentlyVisible && currentlyVisibleCount === 1) {
          return prev;
        }

        return {...prev, [field]: !currentlyVisible};
      });
    },
    [setVisibility]
  );

  const items = useMemo(
    () =>
      baseColumns.map((c) => {
        const checked = Boolean(visibility[c.field]);
        const disabled = checked && visibleCount === 1;
        return {
          id: c.field,
          label: c.headerName || '',
          checked,
          disabled,
          onToggle: () => toggle(c.field),
        };
      }),
    [baseColumns, visibility, visibleCount, toggle]
  );

  const handleMoreClick = useCallback(
    (e: React.MouseEvent<HTMLElement>, row: ICodingListGridData) => {
      e.stopPropagation();
      setSelectedForm(row);
      menuRef1.current?.open(e.currentTarget as HTMLElement);
    },
    []
  );

  // Implement handleEditClick using useCodingFormDetailsforEdit
  const handleEditClick = useCallback(
    async (codingFormId: string) => {
      try {
        const response =
          await codingFormDetailsMutation.mutateAsync(codingFormId);
        if (response?.data) {
          onEditForm?.(response.data);
        } else {
          setToastMessage('Failed to fetch coding form details');
          setToastStatus([ToastType.Error]);
          setShowToast(true);
        }
      } catch (error: any) {
        setToastMessage(error?.message || 'Failed to fetch coding form');
        setToastStatus([ToastType.Error]);
        setShowToast(true);
      }
    },
    [codingFormDetailsMutation]
  );

  const handleMenuItemClick = useCallback(
    (item: {id: string}) => {
      if (!selectedForm) return;
      if (item.id === 'edit') {
        handleEditClick(selectedForm.codingFormId);
      }
      if (item.id === 'copy') {
        setOpenCopyModal(true);
      }
      if (item.id === 'delete') {
        handleDeleteClick(selectedForm.codingFormId);
      }
      menuRef1.current?.close();
    },
    [selectedForm, handleEditClick]
  );

  const renderActionsCell = useCallback((params: GridRenderCellParams) => {
    const row = params.row as ICodingListGridData;

    return (
      <ArrIconButton
        onClick={(e) => {
          handleMoreClick(e, row);
        }}
        icon={<MoreIcon />}
        ariaLabel={t('common.moreOptions')}
        shape="none"
        className="user-grid-icon-button"
        testId="manage-coding-forms-more-options-icon-button"
      />
    );
  }, []);

  const updatedColumns = useMemo(
    () => [
      ...baseColumns,
      {
        field: '',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        headerClassName: 'typo-text label2_size more-option-header',
        renderCell: renderActionsCell,
        cellClassName: 'action-cell',
      },
    ],
    [baseColumns, renderActionsCell]
  );

  const menuRef1 = useRef<ArrMenuHandle | null>(null);
  const menuRef2 = useRef<ArrMenuHandle | null>(null);

  // const handleManageColumnsClick = (e: React.MouseEvent<HTMLElement>) => {
  //   menuRef2.current?.open(e.currentTarget as HTMLElement);
  // };

  const handlePerPageChange = useCallback((newPerPage: number) => {
    setItemsPerPage(newPerPage);
  }, []);

  const handlePageNumberChange = useCallback((newPageNumber: number) => {
    setPageNumber(newPageNumber);
  }, []);

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
  }, []);

  const handleColumnVisibilityModelChange = (
    newModel: GridColumnVisibilityModel
  ) => {
    const visible = Object.values(newModel ?? {}).filter(Boolean).length;
    if (visible === 0) {
      return;
    }
    setVisibility(newModel);
  };

  const triggerToast = (message: string, status: ToastType[]) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };
  return (
    <Box className="manage-coding-forms-container">
      <Box className="grid-header">
        <Box className="grid-pagination">
          <ArrPagination
            perPageOptions={[10, 25, 50, 100]}
            totalItems={codingFormList?.totalRecords ?? 0}
            currentPage={pageNumber}
            onPageChange={handlePageNumberChange}
            perPage={itemsPerPage}
            onPerPageChange={handlePerPageChange}
            mode="wide"
            entityType="entries"
            showPerPageOptions
            alwaysShow={true}
            isFetching={isCodingFormListLoading}
          />
        </Box>

        <Box className="right-button-container">
          <ArrMenu
            id="more-menu-2"
            ref={menuRef2}
            items={items}
            direction="bottom-right"
            className="manage-coding-columns"
            keepOpenOnItemClick={true}
            renderItem={(item) => (
              <ColumnMenuItem
                id={String(item.id)}
                label={item.label}
                checked={Boolean(item.checked)}
                disabled={Boolean(item.disabled)}
                onToggle={() => item.onToggle?.()}
              />
            )}
          />
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            className="arr-divider"
          />
          <ArrButton
            type="submit"
            variant="secondary"
            title="Drawer"
            size="small"
            onClick={() => {
              setIsSecurityGroupForm(true);
            }}
            dataTestId="manage-coding-forms-security-group-button-button"
          >
            {t('codingForms.manage.securityGroupButton')}
          </ArrButton>
        </Box>
      </Box>
      <Box className="grid-main">
        <ArrDataGrid
          rowHeight={ROW_HEIGHT}
          rows={rows}
          columns={updatedColumns}
          currentPage={pageNumber}
          itemsPerPage={itemsPerPage}
          pageSize={itemsPerPage}
          entityType={'codingForm'}
          primaryKey="codingFormId"
          onSortModelChange={handleSortModelChange}
          sortModel={sortModel}
          className="manage-coding-forms-grid"
          noRowsOverlay={NoRowsOverlay}
          hideFooter
          columnVisibilityModel={visibility}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          loading={isCodingFormListLoading}
          disableVirtualization={true}
        />
      </Box>
      <ArrMenu
        id="more-menu"
        ref={menuRef1}
        items={codingFormsDetailsMoreMenu()}
        onItemClick={handleMenuItemClick}
        direction="left"
      />
      <DeleteCodingFormModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        selectedForm={selectedForm}
        triggerToast={triggerToast}
        refetchCodingFormList={refetchCodingFormList}
      />
      <CopyCodingFormModal
        open={openCopyModal}
        onClose={() => setOpenCopyModal(false)}
        selectedForm={selectedForm}
        triggerToast={triggerToast}
        refetchCodingFormList={refetchCodingFormList}
      />
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={toastStatus}
          onClose={() => setShowToast(false)}
        />
      )}
      {isSecurityGroupForm && (
        <SecurityGroupCodingForm
          showDrawer={isSecurityGroupForm}
          handleDrawer={setIsSecurityGroupForm}
          handleOnSuccess={handleOnSuccess}
        />
      )}
      {showToastNotification && (
        <ArrToastNotification
          message={toastNotificationMessage}
          status={[ToastType.Success]}
          onClose={() => setShowToastNotification(false)}
        />
      )}
    </Box>
  );
};

export default ManageCodingForms;
